import { State } from '@meraki-internal/state';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { ILink } from '../../caremap/WithLinks';

interface INotePinResponse {
    noteId: string;
    pinnedAt: string;
}

interface INotePinData {
    pinnedAt: string;
}

export class NotePinsState extends State<Record<string, never>> {
    private pins: Map<string, INotePinData> = new Map();

    static inject = () => [APIClient];
    constructor(private apiClient: APIClient) {
        super({});
    }

    load = async (notePinsLink: ILink): Promise<void> => {
        const pinsResponse: INotePinResponse[] = await this.apiClient.get(notePinsLink);
        this.pins = new Map(
            pinsResponse.map(pin => [pin.noteId, { pinnedAt: pin.pinnedAt }])
        );
        this.setState({});
    };

    pinNote = async (params: { noteId: string; pinLink: ILink }): Promise<void> => {
        const { noteId, pinLink } = params;

        // Optimistic update
        const now = new Date().toISOString();
        this.pins.set(noteId, { pinnedAt: now });
        this.setState({});

        try {
            const response: INotePinResponse = await this.apiClient.put(pinLink, {});
            // Update with server response
            this.pins.set(noteId, { pinnedAt: response.pinnedAt });
            this.setState({});
        } catch (error) {
            // Revert optimistic update on error
            this.pins.delete(noteId);
            this.setState({});
            throw error;
        }
    };

    unpinNote = async (params: { noteId: string; pinLink: ILink }): Promise<void> => {
        const { noteId, pinLink } = params;

        // Store for potential revert
        const previousPin = this.pins.get(noteId);

        // Optimistic update
        this.pins.delete(noteId);
        this.setState({});

        try {
            await this.apiClient.delete(pinLink, {});
        } catch (error) {
            // Revert optimistic update on error
            if (previousPin) {
                this.pins.set(noteId, previousPin);
                this.setState({});
            }
            throw error;
        }
    };

    isPinned = (noteId: string): boolean => {
        return this.pins.has(noteId);
    };

    getPinnedAt = (noteId: string): string | undefined => {
        return this.pins.get(noteId)?.pinnedAt;
    };

    getPinnedNoteIds = (): string[] => {
        return Array.from(this.pins.keys());
    };

    getPinsCount = (): number => {
        return this.pins.size;
    };
}
